import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { BackChevron } from '../components/BackChevron';
import {
  ApiError,
  initiatePayment,
  paymentCallback,
  type OrderSummary,
  type PaymentCallbackResponse,
} from '../lib/api-client';
import type { TranslationKey } from '../lib/i18n';
import { useI18n } from '../lib/i18n-context';

/** 模擬第三方 provider_transaction_id（demo 用；正式接金流時由 PSP 回傳）。 */
function mockProviderTxId(): string {
  const ts = Date.now().toString(36);
  const r = Math.random().toString(36).slice(2, 10);
  return `mock_${ts}_${r}`;
}

/** 非成功結果（oversold / failed）之文案 key。 */
interface OutcomeCopy {
  titleKey: TranslationKey;
  tone: 'warn' | 'error';
  lineKeys: TranslationKey[];
}

/**
 * 把後端 payment-callback 之非 paid outcome 對應到 i18n key。
 *
 * - `oversold`：付款瞬間班次售罄。後端走 AC-B1-10 自動退款 + 通知 fanout，
 *   買家無須另外申請退票。
 * - `failed`：付款未完成，訂單未成立；引導返回重新下單。
 */
function describeOutcome(outcome: PaymentCallbackResponse['outcome']): OutcomeCopy {
  if (outcome === 'oversold') {
    return {
      titleKey: 'checkout.oversoldTitle',
      tone: 'warn',
      lineKeys: ['checkout.oversoldLine1', 'checkout.oversoldLine2'],
    };
  }
  // failed
  return {
    titleKey: 'checkout.failedTitle',
    tone: 'error',
    lineKeys: ['checkout.failedLine1', 'checkout.failedLine2'],
  };
}

/**
 * 模擬付款頁。
 *
 * 1. POST /orders/:id/events { event: 'initiate_payment' } → HOLD → PAYMENT_PENDING
 * 2. POST /orders/:id/payment-callback { status: 'success', provider_transaction_id }
 *    → PAYMENT_PENDING → PAID + 同 tx 發 ticket
 * 3. outcome:
 *    - `paid`     → 帶 issuedTicketIds 導向 /orders/:id/confirm
 *    - `oversold` / `failed` → 留在本頁，渲染買家友善結果面板
 */
export function CheckoutPage(): JSX.Element {
  const { id: orderId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [callbackResult, setCallbackResult] = useState<PaymentCallbackResponse | null>(null);

  const payMutation = useMutation({
    mutationFn: async (): Promise<PaymentCallbackResponse> => {
      if (orderId === undefined) throw new Error('missing order id');
      // step 1：HOLD → PAYMENT_PENDING
      await initiatePayment(orderId);
      // step 2：PAYMENT_PENDING → PAID（同 tx 發 ticket）
      return paymentCallback(orderId, mockProviderTxId());
    },
    onSuccess: (res) => {
      setCallbackResult(res);
      if (res.outcome === 'paid' && orderId !== undefined) {
        // 用 navigate state 傳遞 issuedTicketIds → OrderConfirm 不需再撈
        navigate(`/orders/${orderId}/confirm`, {
          replace: true,
          state: {
            issuedTicketIds: res.issuedTicketIds ?? [],
            order: res.order,
          },
        });
      }
    },
  });

  // oversold / failed 為終態：不再給重試 CTA，改渲染結果面板。
  const settledOutcome =
    callbackResult !== null && callbackResult.outcome !== 'paid' ? callbackResult : null;

  return (
    <div className="app-shell">
      <header className="topbar topbar-lead">
        <BackChevron to="/" label={t('common.backHome')} />
        <h1>{t('checkout.title')}</h1>
      </header>

      {settledOutcome !== null ? (
        <PaymentOutcomePanel result={settledOutcome} />
      ) : (
        <section className="card">
          <p className="kv-label" style={{ margin: '0 0 6px' }}>
            {t('checkout.orderNo')}
          </p>
          <div
            style={{
              background: 'var(--boat-bg)',
              border: '1px solid var(--boat-border-light)',
              borderRadius: 8,
              padding: '11px 12px',
              fontFamily: 'monospace',
              fontSize: 13,
              wordBreak: 'break-all',
            }}
          >
            {orderId}
          </div>
          <p style={{ color: '#777', fontSize: 13 }}>{t('checkout.demoNote')}</p>

          {payMutation.isError && (
            <div className="error-text">
              {t('checkout.reqFailed')}
              {payMutation.error instanceof ApiError
                ? (payMutation.error.problem?.detail ?? payMutation.error.message)
                : t('common.unknownError')}
              {t('checkout.retryHint')}
            </div>
          )}

          <button
            type="button"
            className="btn-primary btn-block"
            style={{ marginTop: 12 }}
            disabled={payMutation.isPending || orderId === undefined}
            onClick={() => payMutation.mutate()}
          >
            {payMutation.isPending
              ? t('checkout.pending')
              : payMutation.isError
                ? t('checkout.retry')
                : t('checkout.pay')}
          </button>
        </section>
      )}
    </div>
  );
}

/** oversold / failed 之結果面板。 */
function PaymentOutcomePanel({ result }: { result: PaymentCallbackResponse }): JSX.Element {
  const { t } = useI18n();
  const copy = describeOutcome(result.outcome);
  const accent = copy.tone === 'warn' ? '#b8860b' : '#D93025';
  return (
    <section className="card" style={{ borderLeft: `4px solid ${accent}` }}>
      <h2
        style={{
          margin: '0 0 8px',
          color: accent,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <WarningMark color={accent} />
        {t(copy.titleKey)}
      </h2>
      {copy.lineKeys.map((lineKey) => (
        <p key={lineKey} style={{ margin: '0 0 6px' }}>
          {t(lineKey)}
        </p>
      ))}
      <p style={{ color: '#777', fontSize: 13, marginTop: 8 }}>
        {t('checkout.orderState', { id: result.order.id, state: result.order.state })}
      </p>
      <Link
        to="/"
        className="btn-primary btn-block"
        style={{ textDecoration: 'none', marginTop: 12 }}
      >
        {t('checkout.backReselect')}
      </Link>
    </section>
  );
}

/** 警示三角驚嘆號 — oversold / failed 結果面板標題用。 */
function WarningMark({ color }: { color: string }): JSX.Element {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

// re-export used by router typing
export type { OrderSummary };
